import { createContext, useEffect } from "react";
import { useRecoilState } from "recoil";

import { authStateAtom } from '@states/auth';
import axios from "@utils/axios";
import { isValidToken, setSession } from "@utils/jwt";
import { UserType } from '../types/auth';

const AuthContext = createContext({});

function AuthProvider({ children }) {
  const [authStates, setAuthStates] = useRecoilState(authStateAtom);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await axios.get("users/me");
          const user: UserType = response.data;

          // 권한은 서버가 계산한 것을 그대로 담는다. 여기서 등급·기간을 다시 보지 않는다.
          // 서버가 안 주면 **빈 배열**이다 — 권한 시스템은 모르면 막는 쪽이 안전하다.
          setAuthStates({
            isInitialized: true,
            isAuthenticated: true,
            permissions: user.permissions ?? [],
            user,
          });
        } else {
          setAuthStates({
            isInitialized: true,
            isAuthenticated: false,
            permissions: [],
            user: null,
          });
        }
      } catch (err) {
        console.error(err);
        setAuthStates({
          isInitialized: true,
          isAuthenticated: false,
          permissions: [],
          user: null,
        });
      }
    };
    initialize();

    // ★ **다른 탭**에서 로그아웃하거나 다른 계정으로 들어오면 이 탭도 따라간다(f1-7).
    //   요청 인터셉터는 요청마다 저장소의 토큰을 읽는데 화면의 이름·권한은 이 탭이 처음 읽은
    //   사람 그대로라, 탭 A 는 X 로 보이면서 요청은 Y 의 토큰으로 나갔다.
    //   `storage` 이벤트는 **다른 탭**이 바꿨을 때만 온다.
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === "accessToken") initialize();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [setAuthStates])


  const register = () => {};

  const logout = async () => {
    setSession(null);

    setAuthStates({
      isInitialized: true,
      isAuthenticated: false,
      permissions: [],
      user: null,
    });
  };

  const resetPassword = () => {};

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...authStates,
        method: "jwt",
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
